/** Menu blips and game cues (05 §2): short pulse and triangle figures, as C64 menus beeped. Pure. */

import { midiToHz } from '../notes';
import { vary } from '../rand';
import { adsr, type VoiceSpec } from '../voice';
import { hit, type Sfx, tone } from './kit';

/** A figure of notes played at `stepTime` on one voice, holding the last. */
function figure(midis: readonly number[], stepTime: number, gate: number, level: number, width = 0.25): VoiceSpec {
  const steps = midis.map(midiToHz);
  return {
    ...tone('pulse', steps[0] ?? 440, gate, adsr(0.001, 0.1, 0.35, 0.06), level),
    steps,
    stepTime,
    once: true,
    width,
  };
}

/** Moving through a menu: the smallest tick, E6. */
export const move: Sfx = (r) => [
  { ...hit('pulse', vary(r, midiToHz(88), 0.01), 0.04, 0.14), width: 0.25 },
];

/** Choosing: up a fifth. */
export const select: Sfx = () => [figure([84, 91], 0.045, 0.1, 0.2)];

/** Going back: down a fifth. */
export const back: Sfx = () => [figure([79, 72], 0.045, 0.1, 0.18)];

/** Not allowed: a short low buzz. */
export const error: Sfx = () => {
  const buzz = (hz: number): VoiceSpec => ({
    ...tone('pulse', hz, 0.14, adsr(0.002, 0.05, 0.8, 0.04), 0.18),
    width: 0.5,
    filter: { type: 'lowpass', freq: 1200, q: 0 },
  });
  return [buzz(110), buzz(117)];
};

/** A key typed in the initials entry or the editor: a thin click. */
export const type: Sfx = (r) => [
  { ...hit('pulse', vary(r, 2200, 0.08), 0.02, 0.1), width: 0.1 },
];

/** An extra life: a quick major arpeggio up two octaves, doubled an octave below. */
export const bonusLife: Sfx = () => {
  const notes = [72, 76, 79, 84, 88, 91, 96];
  const lead = { ...figure(notes, 0.045, 0.4, 0.26, 0.3), env: adsr(0.002, 0.2, 0.6, 0.15) };
  const low = { ...figure(notes.map((m) => m - 12), 0.045, 0.4, 0.14, 0.5), wave: 'triangle' as const };
  return [lead, low];
};

/** The time-bonus countdown's blip: a quarter-tone higher each tick, up to two octaves above C5. */
export function bonusTickMidi(n: number): number {
  return 72 + Math.min(Math.max(0, n), 48) / 2;
}

export const bonusTick: Sfx = (_r, n) => [
  { ...hit('pulse', midiToHz(bonusTickMidi(n)), 0.035, 0.16), width: 0.5 },
];

/** Pause: a soft falling fourth. */
export const pause: Sfx = () => [{ ...figure([79, 74], 0.07, 0.14, 0.2), wave: 'triangle' }];

/** Resume: the same fourth rising. */
export const resume: Sfx = () => [{ ...figure([74, 79], 0.07, 0.14, 0.2), wave: 'triangle' }];

/** A game starts: a bright flourish up C major. */
export const start: Sfx = () => [
  { ...figure([72, 79, 84, 88, 91], 0.06, 0.35, 0.24, 0.4), env: adsr(0.002, 0.2, 0.5, 0.2) },
];
